import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { NzIconModule } from 'ng-zorro-antd/icon';

import { ProgressService } from '../services/progress.service';
import {
  ProgressMilestone,
  getMilestoneRoute,
  getMilestoneQueryParams,
} from './progress-navigation.utils';

@Component({
  selector: 'app-onboarding-progress-widget',
  standalone: true,
  imports: [CommonModule, TranslateModule, NzIconModule],
  templateUrl: './onboarding-progress-widget.component.html',
  styleUrls: ['./onboarding-progress-widget.component.less'],
})
export class OnboardingProgressWidgetComponent implements OnInit, OnDestroy {
  private readonly DISMISS_STORAGE_KEY = 'onboarding_widget_dismissed';
  private readonly AUTO_EXPAND_SEEN_STORAGE_KEY = 'onboarding_widget_auto_expand_seen';

  progress = 0;
  loaded = false;
  dismissed = false;
  expanded = false;
  milestones: ProgressMilestone[] = [];
  incompleteMilestones: ProgressMilestone[] = [];

  private subscription?: Subscription;

  constructor(
    private progressService: ProgressService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.dismissed = sessionStorage.getItem(this.DISMISS_STORAGE_KEY) === 'true';
    if (this.dismissed) {
      return;
    }

    this.fetchProgress();
    this.subscription = this.progressService.onProgressUpdate().subscribe(() => {
      this.fetchProgress();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private fetchProgress(): void {
    this.progressService.getProgress().subscribe({
      next: (data: any) => {
        this.progress = Number(data?.progress ?? 0);
        this.loaded = true;

        this.milestones = Array.isArray(data?.milestones)
          ? data.milestones
          : [];

        this.incompleteMilestones = this.milestones.filter((m) => !m.completed);

        if (this.progress >= 100) {
          this.dismissed = false;
          this.expanded = false;
          sessionStorage.removeItem(this.DISMISS_STORAGE_KEY);
          sessionStorage.removeItem(this.AUTO_EXPAND_SEEN_STORAGE_KEY);
          return;
        }

        this.maybeAutoExpand();
      },
      error: () => {
        this.loaded = false;
      },
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.expanded) {
      this.expanded = false;
    }
  }

  private maybeAutoExpand(): void {
    const seen = sessionStorage.getItem(this.AUTO_EXPAND_SEEN_STORAGE_KEY) === 'true';

    if (!seen && this.progress < 100 && !this.dismissed) {
      this.expanded = true;
      sessionStorage.setItem(this.AUTO_EXPAND_SEEN_STORAGE_KEY, 'true');
    }
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  dismiss(event: MouseEvent): void {
    event.stopPropagation();
    this.dismissed = true;
    this.expanded = false;
    sessionStorage.setItem(this.DISMISS_STORAGE_KEY, 'true');
  }

  isMilestoneLocked(milestone: ProgressMilestone): boolean {
    if (milestone.key === 'properties') {
      const companyDone = this.milestones.find((m) => m.key === 'companyInfo')?.completed;
      const templateDone = this.milestones.find((m) => m.key === 'templateHeader')?.completed;
      return !(companyDone && templateDone);
    }

    if (milestone.key === 'hospitalityProperties') {
      const companyDone = this.milestones.find((m) => m.key === 'companyInfo')?.completed;
      const landingDone = this.milestones.find((m) => m.key === 'hospitalityLanding')?.completed;
      return !(companyDone && landingDone);
    }

    return false;
  }

  get firstUnlockedIncompleteMilestone(): ProgressMilestone | null {
    return this.incompleteMilestones.find((m) => !this.isMilestoneLocked(m)) ?? null;
  }

  handleMilestoneClick(milestone: ProgressMilestone): void {
    if (milestone.completed || this.isMilestoneLocked(milestone)) {
      return;
    }

    const route = getMilestoneRoute(milestone.key);
    const queryParams = getMilestoneQueryParams(milestone.key, {
      autoOpenAddProperty: milestone.key === 'properties',
    });

    this.router.navigate([route], { queryParams });
  }

  handleContinueClick(): void {
    const next = this.firstUnlockedIncompleteMilestone;
    if (!next) return;
    this.handleMilestoneClick(next);
  }

  get isVisible(): boolean {
    return this.loaded && this.progress < 100 && !this.dismissed;
  }
}
